import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, X, ShieldCheck } from 'lucide-react';
const names = ['Tomás G.', 'Facundo R.', 'Martín P.', 'Joaquín D.', 'Santiago L.', 'Ignacio M.', 'Lucas B.', 'Mateo F.', 'Nicolás A.', 'Bautista S.', 'Agustín V.', 'Juan Cruz H.', 'Franco O.', 'Manuel T.', 'Valentino C.', 'Gonzalo E.', 'Benjamín N.', 'Felipe J.', 'Ramiro Q.', 'Tobías U.', 'Valentina F.', 'Camila R.', 'Sofía M.', 'Delfina A.'];
const locations = ['Buenos Aires', 'Rosario, Santa Fe', 'Córdoba Capital', 'Mendoza', 'La Plata, BA', 'San Isidro, BA', 'Mar del Plata', 'Tucumán', 'Salta', 'Bahía Blanca', 'San Juan', 'Neuquén', 'Pilar, BA', 'Tigre, BA', 'Resistencia, Chaco', 'Posadas, Misiones', 'Paraná, Entre Ríos', 'Santa Fe Capital', 'Bariloche', 'Comodoro Rivadavia', 'Villa Carlos Paz', 'Río Cuarto', 'San Luis', 'Concordia'];
const products = [{
  name: 'Rugby Pro Kit',
  color: 'text-slate-200',
  isElite: false
}, {
  name: 'Rugby Pro Kit Élite',
  color: 'text-rugby-gold-400',
  isElite: true
}];
const timeAgo = ['hace 12 segundos', 'hace 38 segundos', 'hace 1 minuto', 'hace 2 minutos', 'hace 3 minutos', 'hace 5 minutos'];
const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
interface Purchase {
  name: string;
  location: string;
  product: (typeof products)[number];
  time: string;
}
export function PurchasePopup() {
  const [visible, setVisible] = useState(false);
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  useEffect(() => {
    let hideTimeout: ReturnType<typeof setTimeout>;
    let nextTimeout: ReturnType<typeof setTimeout>;
    const showNext = () => {
      // Elite chance slightly higher to drive upsell
      const product = Math.random() < 0.55 ? products[1] : products[0];
      setPurchase({
        name: pick(names),
        location: pick(locations),
        product,
        time: pick(timeAgo)
      });
      setVisible(true);
      // Hide after ~5.5s
      hideTimeout = setTimeout(() => {
        setVisible(false);
        // Wait random 40-60s before next
        const delay = 40000 + Math.random() * 20000;
        nextTimeout = setTimeout(showNext, delay);
      }, 5500);
    };
    // First popup after 8s (let user see the page first)
    nextTimeout = setTimeout(showNext, 8000);
    return () => {
      clearTimeout(hideTimeout);
      clearTimeout(nextTimeout);
    };
  }, []);
  const handleClose = () => setVisible(false);
  return <AnimatePresence data-id="element-332">
      {visible && purchase && <motion.div initial={{
      opacity: 0,
      x: -40,
      y: 20
    }} animate={{
      opacity: 1,
      x: 0,
      y: 0
    }} exit={{
      opacity: 0,
      x: -40,
      y: 20
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="fixed bottom-4 left-4 right-4 sm:right-auto sm:bottom-6 sm:left-6 z-50 max-w-sm" role="status" aria-live="polite" data-id="element-333">
          <div className="bg-rugby-green-900 border border-rugby-gold-500/40 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] overflow-hidden backdrop-blur-sm" data-id="element-334">
            {/* Header strip */}
            <div className="flex items-center justify-between bg-rugby-green-950 px-3 py-1.5 border-b border-rugby-green-800" data-id="element-335">
              <div className="flex items-center gap-1.5" data-id="element-336">
                <ShieldCheck size={12} className="text-rugby-gold-400" data-id="element-337" />
                <span className="text-[10px] font-bold text-rugby-gold-400 uppercase tracking-wider" data-id="element-338">
                  Compra Verificada
                </span>
              </div>
              <button onClick={handleClose} aria-label="Cerrar notificación" className="text-slate-500 hover:text-slate-300 transition-colors p-0.5 -mr-1" data-id="element-339">
                <X size={14} data-id="element-340" />
              </button>
            </div>

            {/* Body */}
            <div className="p-3 flex items-start gap-3" data-id="element-341">
              {/* Avatar / Icon */}
              <div className="flex-shrink-0 mt-0.5" data-id="element-342">
                <div className="relative" data-id="element-343">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rugby-gold-400 to-rugby-gold-600 flex items-center justify-center text-rugby-green-950 font-display text-base shadow-lg" data-id="element-344">
                    {purchase.name.charAt(0)}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-rugby-green-900 flex items-center justify-center" data-id="element-345">
                    <CheckCircle2 size={14} className="text-rugby-gold-400 fill-rugby-green-950" data-id="element-346" />
                  </div>
                </div>
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0" data-id="element-347">
                <p className="text-sm text-white leading-snug" data-id="element-348">
                  <span className="font-bold" data-id="element-349">{purchase.name}</span>
                  <span className="text-slate-400" data-id="element-350"> de </span>
                  <span className="font-medium text-slate-200" data-id="element-351">
                    {purchase.location}
                  </span>
                </p>
                <p className="text-xs text-slate-300 mt-0.5 leading-snug" data-id="element-352">
                  acaba de comprar el{' '}
                  <span className={`font-bold ${purchase.product.color}`} data-id="element-353">
                    {purchase.product.name}
                  </span>
                  {purchase.product.isElite && ' ⭐'}
                </p>
                <div className="flex items-center gap-1.5 mt-1.5" data-id="element-354">
                  <span className="flex items-center gap-1 text-[10px] text-slate-500" data-id="element-355">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-356" />
                    🇦🇷 {purchase.time}
                  </span>
                </div>
              </div>
            </div>

            {/* Progress bar (visual countdown until auto-hide) — uses scaleX
                instead of width so framer-motion does not have to resolve a
                percentage against the parent's computed width (which can be
                null while AnimatePresence is mounting and trigger the
                complex-mix crash). */}
            <motion.div initial={{
          scaleX: 1
        }} animate={{
          scaleX: 0
        }} transition={{
          duration: 5.5,
          ease: 'linear'
        }} style={{
          transformOrigin: '0% 50%'
        }} className="h-0.5 w-full bg-rugby-gold-500/60" data-id="element-357" />
          </div>
        </motion.div>}
    </AnimatePresence>;
}